import { defineStore } from "pinia";
import { Bet, BetRequest } from "@/common/models/bet";
import { Group } from "@/common/models/group";
import { Player } from "@/common/models/player";
import {
  fetchPlayerBets,
  fetchPlayerMeBets,
  createBet,
  updateBet,
} from "@/common/services/bets.service";
import { useMatchesStore } from "@/common/store/matches.store";
import { useErrorStore } from "@/common/store/error.store";
import { upsertEntities, removeByProp } from "@/common/store/utils";

interface BetsState {
  ids: string[];
  entities: { [id: string]: Bet };
}

export const useBetsStore = defineStore("bets", {
  state: (): BetsState => ({
    ids: [],
    entities: {},
  }),

  getters: {
    all(): Bet[] {
      const matches = useMatchesStore();
      return this.ids.map((id) => {
        const entity = this.entities[id];
        const matchId = entity.match as string;
        const match = matches.allEntities[matchId];
        return {
          ...entity,
          match,
        };
      });
    },
  },

  actions: {
    async fetchPlayerBets(player: Player): Promise<void> {
      try {
        const bets = await fetchPlayerBets(player);
        this.fetchPlayerBetsSuccess(player, bets);
      } catch (error) {
        this.fetchPlayerBetsError(error);
      }
    },

    fetchPlayerBetsSuccess(player: Player, bets: Bet[]): void {
      removeByProp("player", player.id, this);
      upsertEntities(bets, this);
    },

    fetchPlayerBetsError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`fetchPlayerBetsError: ${err.toString()}`);
    },

    async fetchPlayerMeBets(group: Group): Promise<void> {
      try {
        const bets = await fetchPlayerMeBets(group);
        this.fetchPlayerMeBetsSuccess(bets);
      } catch (error) {
        this.fetchPlayerMeBetsError(error);
      }
    },

    fetchPlayerMeBetsSuccess(bets: Bet[]): void {
      upsertEntities(bets, this);
    },

    fetchPlayerMeBetsError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`fetchPlayerMeBetsError: ${err.toString()}`);
    },

    async createBet(request: BetRequest, group: Group): Promise<void> {
      try {
        const bet = await createBet(request, group);
        this.createBetSuccess(bet);
      } catch (error) {
        this.createBetError(error);
      }
    },
    createBetSuccess(bet: Bet): void {
      upsertEntities([bet], this);
    },
    createBetError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`createBetError: ${err.toString()}`);
    },
    async updateBet(request: BetRequest, group: Group): Promise<void> {
      try {
        const bet = await updateBet(request, group);
        this.updateBetSuccess(bet);
      } catch (error) {
        this.updateBetError(error);
      }
    },
    updateBetSuccess(bet: Bet): void {
      upsertEntities([bet], this);
    },
    updateBetError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`createBetError: ${err.toString()}`);
    },
  },
});
